/**
 * Firestore Helper Utilities
 * Provides common utilities for Firestore operations including error handling
 */

import { query, collection, getDocs } from 'firebase/firestore';

/**
 * Handles Firestore index errors by falling back to manual sorting
 * @param {Error} error - The error from Firestore
 * @param {string} collectionName - Name of the collection
 * @param {Function} fallbackQuery - Function to execute fallback query
 * @param {Function} sortFunction - Function to manually sort results
 * @param {Function} errorCallback - Optional error callback
 * @returns {Promise<Array>} - Sorted results
 */
export const handleIndexError = async (error, collectionName, fallbackQuery, sortFunction, errorCallback) => {
  if (error.code === 'failed-precondition') {
    console.warn(`Index missing for ${collectionName}. Using simple query without sorting.`);
    try {
      const querySnapshot = await fallbackQuery();
      const results = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const sorted = sortFunction ? sortFunction(results) : results;
      return sorted;
    } catch (fallbackError) {
      if (errorCallback) {
        errorCallback(fallbackError);
      } else {
        console.error(`Error in ${collectionName} fallback query:`, fallbackError);
      }
      throw fallbackError;
    }
  }
  throw error;
};

/**
 * Sorts documents by timestamp field
 * @param {Array} docs - Array of documents
 * @param {string} field - Timestamp field name (default: 'createdAt')
 * @param {string} direction - 'asc' or 'desc' (default: 'desc')
 * @returns {Array} - Sorted documents
 */
export const sortByTimestamp = (docs, field = 'createdAt', direction = 'desc') => {
  return docs.sort((a, b) => {
    const aTime = a[field]?.toMillis?.() || a[field]?.getTime?.() || new Date(a[field] || 0).getTime();
    const bTime = b[field]?.toMillis?.() || b[field]?.getTime?.() || new Date(b[field] || 0).getTime();
    return direction === 'desc' ? bTime - aTime : aTime - bTime;
  });
};

/**
 * Sorts documents by date string field
 * @param {Array} docs - Array of documents
 * @param {string} field - Date field name
 * @param {string} direction - 'asc' or 'desc' (default: 'asc')
 * @returns {Array} - Sorted documents
 */
export const sortByDateString = (docs, field, direction = 'asc') => {
  return docs.sort((a, b) => {
    const aTime = a[field] ? new Date(a[field]).getTime() : 0;
    const bTime = b[field] ? new Date(b[field]).getTime() : 0;
    return direction === 'asc' ? aTime - bTime : bTime - aTime;
  });
};

/**
 * Creates a safe query with fallback for missing indexes
 * @param {Object} db - Firestore database instance
 * @param {string} collectionPath - Collection path
 * @param {Array} queryConstraints - Array of query constraints
 * @param {Function} errorCallback - Optional error callback
 * @returns {Promise<Array>} - Query results
 */
export const safeQuery = async (db, collectionPath, queryConstraints = [], errorCallback) => {
  try {
    const collectionRef = collection(db, collectionPath);
    const q = query(collectionRef, ...queryConstraints);
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    // Check if it's an index error
    if (error.code === 'failed-precondition') {
      console.warn(`Index missing for ${collectionPath}. Using simple query.`);
      try {
        // Try without orderBy constraints
        const simpleConstraints = queryConstraints.filter(
          constraint => constraint.type !== 'orderBy'
        );
        const collectionRef = collection(db, collectionPath);
        const simpleQuery = queryConstraints.length > 0 && simpleConstraints.length > 0
          ? query(collectionRef, ...simpleConstraints)
          : collectionRef;
        const querySnapshot = await getDocs(simpleQuery);
        const results = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Find orderBy constraint and sort manually
        const orderByConstraint = queryConstraints.find(c => c.type === 'orderBy');
        if (orderByConstraint) {
          const field = orderByConstraint.field;
          const direction = orderByConstraint.direction || 'asc';
          return sortByTimestamp(results, field, direction);
        }
        
        return results;
      } catch (fallbackError) {
        if (errorCallback) {
          errorCallback(fallbackError);
        } else {
          console.error(`Error in ${collectionPath} fallback query:`, fallbackError);
        }
        throw fallbackError;
      }
    }
    throw error;
  }
};

/**
 * Converts Firestore timestamp to date string
 * @param {Object|Date|string} timestamp - Firestore timestamp, Date, or string
 * @returns {string} - Date string in YYYY-MM-DD format
 */
export const timestampToDateString = (timestamp) => {
  if (!timestamp) return '';
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate().toISOString().split('T')[0];
  }
  if (timestamp instanceof Date) {
    return timestamp.toISOString().split('T')[0];
  }
  return timestamp;
};

/**
 * Converts Firestore timestamp to datetime string
 * @param {Object|Date|string} timestamp - Firestore timestamp, Date, or string
 * @returns {string} - Datetime string in YYYY-MM-DDTHH:mm format
 */
export const timestampToDateTimeString = (timestamp) => {
  if (!timestamp) return '';
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate().toISOString().slice(0, 16);
  }
  if (timestamp instanceof Date) {
    return timestamp.toISOString().slice(0, 16);
  }
  return timestamp;
};

